// マイクの getUserMedia を一元管理するサービス。
// - 許可状態とデバイス一覧はストア（micPermission / audioDevices）に反映する
// - 録音セッション中の MediaStream はここが唯一のオーナーとなり、
//   録音・波形描画・音声認識が同じストリームを共有する
import { useAppStore } from "../store/appStore";

const DEVICE_LABEL_SETTLE_MS = 100; // QUIRK: 許可直後はラベルが空のことがあるため少し待ってから列挙する

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const buildConstraints = (deviceId?: string): MediaStreamConstraints => ({
	audio: deviceId ? { deviceId: { exact: deviceId } } : true,
});

/** 録音セッション中の共有ストリーム（オーナーはこのモジュールのみ） */
let currentStream: MediaStream | null = null;

/**
 * マイクデバイス一覧を列挙してストアに反映する。
 * まだマイクが選択されていなければ先頭のデバイスを自動選択する。
 */
const refreshDevices = async (): Promise<void> => {
	const devices = await navigator.mediaDevices.enumerateDevices();
	const mics = devices
		.filter((device) => device.kind === "audioinput")
		.map((device) => ({
			deviceId: device.deviceId,
			// QUIRK: 許可がないとラベルが空になる
			label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
		}));

	const store = useAppStore.getState();
	store.setAudioDevices(mics);

	if (mics.length > 0 && !store.selectedMicrophoneId) {
		store.setSelectedMicrophoneId(mics[0].deviceId);
	}
};

/**
 * Permissions API で既存のマイク許可状態を確認する（プロンプトは出さない）。
 * アプリ起動時に一度呼ぶ。許可状態の変化も監視する。
 */
const checkExistingPermission = async (): Promise<void> => {
	try {
		const status = await navigator.permissions.query({ name: "microphone" as PermissionName });

		const apply = async (state: PermissionState) => {
			if (state === "granted") {
				useAppStore.getState().setMicPermission("granted");
				await refreshDevices();
			} else if (state === "denied") {
				useAppStore.getState().setMicPermission("denied");
			}
		};

		await apply(status.state);
		status.onchange = () => {
			apply(status.state);
		};
	} catch {
		// Permissions API 非対応ブラウザでは unknown のまま（ジェスチャ時に確認される）
	}
};

/**
 * マイク許可を取得する。QUIRK: 必ずユーザージェスチャ（ボタンクリック等）から呼ぶこと。
 * 一時的にストリームを開いてすぐ閉じ、デバイス一覧を更新する。
 * @returns 許可が得られたら true
 */
const ensurePermission = async (deviceId?: string): Promise<boolean> => {
	const store = useAppStore.getState();
	if (store.micPermission === "granted") {
		await refreshDevices();
		return true;
	}

	store.setMicPermission("pending");
	try {
		const stream = await navigator.mediaDevices.getUserMedia(buildConstraints(deviceId));

		// QUIRK: ブラウザがデバイス権限情報を更新する時間を確保してから列挙
		await delay(DEVICE_LABEL_SETTLE_MS);
		await refreshDevices();

		// 確認用の一時ストリームなので停止
		for (const track of stream.getTracks()) {
			track.stop();
		}

		useAppStore.getState().setMicPermission("granted");
		return true;
	} catch (error) {
		console.error("マイクアクセスエラー:", error);
		useAppStore.getState().setMicPermission("denied");
		return false;
	}
};

/**
 * 録音セッション用の共有ストリームを開く。既に開いていれば開き直す。
 */
const openStream = async (deviceId?: string): Promise<MediaStream> => {
	closeStream();
	currentStream = await navigator.mediaDevices.getUserMedia(buildConstraints(deviceId));
	return currentStream;
};

/** 共有ストリームを閉じてマイクを解放する */
const closeStream = (): void => {
	if (currentStream) {
		for (const track of currentStream.getTracks()) {
			track.stop();
		}
		currentStream = null;
	}
};

/** 現在の共有ストリーム（セッション外では null） */
const getCurrentStream = (): MediaStream | null => currentStream;

export const audioStreamService = {
	checkExistingPermission,
	ensurePermission,
	refreshDevices,
	openStream,
	closeStream,
	getCurrentStream,
};
